import {
  Bars3Icon,
  ChevronDoubleLeftIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Drawer from "../../shared/Drawer";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import useWindowSize from "../../hooks/useWindowSize";
import Spinner from "../../shared/Spinner";
import Split from "react-split";

export const SidebarContainer = ({
  footerHeight = 24,
  hideSidebar,
  useViewportHeight = true,
  resizableSidebar = false,
  enableChatOffset = false,
  splitStorageKey,
  defaultSidebarSplitSizes = [72, 28],
  collapsedSidebarMinSize = 48,
  openSidebarMinSize = 320,
  gutterSize = 8,
  children,
  ...props
}) => {
  const parentRef = useRef();
  const childRef = useRef();
  const headerRef = useRef();
  const sidebarRef = useRef(null);
  const { size } = useWindowSize();
  const [open, setOpen] = useState(false);
  const isHidden = size.width < 1024;
  const [headerContext, setHeaderContext] = useState();

  useEffect(() => {
    setHeaderContext(headerRef.current);
  }, [headerRef.current]);

  const [openSplitSizes, setOpenSplitSizes] = useState(() => {
    if (!splitStorageKey) return defaultSidebarSplitSizes;
    try {
      const raw = window.localStorage.getItem(splitStorageKey);
      if (!raw) return defaultSidebarSplitSizes;
      const parsed = JSON.parse(raw);
      if (
        Array.isArray(parsed) &&
        parsed.length === 2 &&
        parsed.every((n) => typeof n === "number")
      ) {
        return parsed;
      }
      return defaultSidebarSplitSizes;
    } catch {
      return defaultSidebarSplitSizes;
    }
  });

  useEffect(() => {
    if (!splitStorageKey) return;
    if (!open) return; // only persist "open" widths
    try {
      window.localStorage.setItem(
        splitStorageKey,
        JSON.stringify(openSplitSizes)
      );
    } catch {
      // ignore storage errors (privacy mode, quota, etc.)
    }
  }, [openSplitSizes, open, splitStorageKey]);

  useEffect(() => {
    if (!enableChatOffset) return;

    const root = document.documentElement;
    const shouldOffset = open && !hideSidebar && !isHidden;

    if (!shouldOffset) {
      root.style.setProperty("--right-sidebar-offset", "0px");
      return undefined;
    }

    const updateOffset = () => {
      const width = Math.round(
        sidebarRef.current?.getBoundingClientRect?.().width || 0
      );
      root.style.setProperty(
        "--right-sidebar-offset",
        width ? `${width}px` : "0px"
      );
    };

    updateOffset();

    if (typeof ResizeObserver === "undefined" || !sidebarRef.current) {
      return () => {
        root.style.setProperty("--right-sidebar-offset", "0px");
      };
    }

    const ro = new ResizeObserver(updateOffset);
    ro.observe(sidebarRef.current);

    return () => {
      ro.disconnect();
      root.style.setProperty("--right-sidebar-offset", "0px");
    };
  }, [enableChatOffset, open, hideSidebar, isHidden]);

  const getContentDimensions = () => {
    if (!parentRef.current || !childRef.current) {
      return {
        parentHeight: 0,
        parentWidth: parentRef.current?.clientWidth || 0,
        offsetTop: headerContext?.offsetTop || 0,
        offsetHeight: headerContext?.offsetHeight || 0,
      };
    }

    return {
      parentHeight: parentRef.current.clientHeight,
      parentWidth: parentRef.current.clientWidth,
      offsetTop: headerContext?.offsetTop || 0,
      offsetHeight: headerContext?.offsetHeight || 0,
    };
  };

  const getContentStyles = () => {
    // When embedded in a fixed-height parent (like DashboardLayout's content
    // area), offsetTop/100vh math can create feedback loops and "shrinking"
    // heights while scrolling. Use simple flex sizing instead.
    if (!useViewportHeight) {
      return {
        wrapperStyle: {
          height: "100%",
          // Important: `minHeight: 100%` can create a subtle overflow loop in
          // nested flex layouts (shows up as 1–2px extra scroll / "whole page"
          // feeling). Let the parent dictate the min-height.
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
        panelStyle: {
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          // Prevent "fall-through" scroll to outer containers when this panel
          // reaches top/bottom.
          overscrollBehavior: "contain",
        },
      };
    }

    const { offsetTop, offsetHeight } = getContentDimensions();

    return {
      wrapperStyle: {
        height: `calc(100% - ${offsetTop}px)`,
        minHeight: `calc(100% - ${offsetTop}px)`,
      },
      panelStyle: {
        height: `calc(100% - ${offsetTop - offsetHeight}px)`,
        overflowY: "auto",
      },
    };
  };

  const getSidebarDimensions = () => {
    if (!parentRef.current || !childRef.current) {
      return {
        parentHeight: 0,
        parentWidth: parentRef.current?.clientWidth || 0,
        offsetTop: headerContext?.offsetTop || 0,
        offsetHeight: headerContext?.offsetHeight || 0,
      };
    }

    return {
      parentHeight: parentRef.current.clientHeight,
      parentWidth: parentRef.current.clientWidth,
      offsetTop: headerContext?.offsetTop || 0,
      offsetHeight: headerContext?.offsetHeight || 0,
    };
  };

  const getSidebarStyles = () => {
    const { offsetTop, offsetHeight } = getSidebarDimensions();
    const footerSpace = props.footer ? footerHeight : 0; // Account for footer if present

    if (!useViewportHeight) {
      return {
        wrapperStyle: {
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
        panelStyle: {
          height: "100%",
          overflow: "hidden",
        },
      };
    }

    return {
      wrapperStyle: {
        height: `calc(100vh - ${offsetTop + offsetHeight + footerSpace}px)`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      },
      panelStyle: {
        height: "100%",
        overflow: "hidden",
      },
    };
  };

  const renderHeader = (headerProps) => {
    if (!headerProps) return null;

    return (
      <div ref={headerRef} onClick={(e) => e.stopPropagation()}>
        {props.header ? (
          props.header({
            ...headerProps,
            ...props,
            headerRef,
            setHeaderContext,
            open,
            setOpen,
          })
        ) : (
          <header className="flex items-center justify-between px-3 py-2 text-semibold text-gray-100">
            <div>{props.title}</div>
            <div className="flex items-center space-x-3">
              {props.actions}
              {!hideSidebar && (
                <div
                  onClick={() => setOpen(true)}
                  className="hover:bg-gray-100 cursor-pointer p-2 rounded-lg block lg:hidden"
                >
                  <ChevronDoubleLeftIcon className="w-5 text-gray-800" />
                </div>
              )}
            </div>
          </header>
        )}
      </div>
    );
  };

  const transition = "transition-all duration-200";

  const asideClasses = classNames(
    // Ensure the collapsed rail (and open sidebar) always spans the full page height
    // even when the sidebar content is hidden.
    "flex flex-col border-l hidden lg:block m-0 flex-shrink-0 h-full overflow-hidden",
    // In embedded mode (inside a constrained parent), `min-h-screen` forces the
    // sidebar to 100vh and creates an unwanted outer scroll.
    useViewportHeight ? "min-h-screen" : "min-h-0",
    transition,
    {
      // When using Split, widths are controlled by the split pane; keep only
      // a minimum collapsed rail width.
      "min-w-[3rem] bg-white hover:bg-gray-100 cursor-pointer": !open,
      // For non-split mode, keep a reasonable default width.
      "w-[18rem] sm:w-[20rem] md:w-[22rem] lg:w-[24rem] xl:w-[28rem]":
        open && !resizableSidebar,
    }
  );
  const childClasses = classNames(
    transition,
    "h-full",
    // " flex-auto",
    {
      "opacity-1": open,
      "opacity-0 hidden": !open,
    }
  );

  const footerClasses = classNames({
    "opacity-1": open,
    "opacity-0": !open,
  });

  const Icon = !open ? Bars3Icon : XMarkIcon;
  const contentClasses = classNames(
    // In a flex-row layout, `w-full` can push the sidebar off-canvas at certain
    // widths. Use flex sizing + min-w-0 so the main content can shrink.
    "flex flex-col flex-1 min-w-0",
    // When rendered inside a constrained parent (e.g. DashboardLayout content
    // area), using `h-screen` creates a second scrollbar. Allow callers to opt
    // into parent-sized height instead.
    useViewportHeight ? "h-screen" : "h-full min-h-0"
    // "overflow-y-auto"
  );
  const iconWrapClasses = classNames(
    transition,
    "p-1 cursor-pointer flex text-gray-700 hover:bg-gray-100 rounded-lg",
    { "justify-center hover:bg-gray-100": !open, "justify-end bg-white": open }
  );
  const topClasses = classNames(
    "flex px-2 items-center",
    // "flex-initial",
    transition,
    {
      "justify-center": !open,
      "justify-between bg-white border-b": open,
    }
  );
  const sideTitleClass = classNames("flex p-[0.24rem] bg-white", transition, {
    hidden: !open,
    block: open,
  });
  // `min-w-0` is critical in flex layouts to allow children (tables/tabs) to
  // shrink instead of forcing the sidebar off screen.
  const childWrapper = classNames("flex flex-grow overflow-hidden min-w-0");

  function handleOpen() {
    setOpen(!open);
  }
  const iconClass = classNames("w-4 w-5 text-gray-800");

  const wrapperClass = classNames(
    // Avoid adding a 1px border to a 100%-height embedded container; it can be
    // enough to create a tiny "outer" scroll in nested layouts.
    "flex w-full min-w-0 box-border",
    useViewportHeight && "border-t",
    // When embedded in a fixed-height parent, ensure this wrapper also has a
    // deterministic height so `getContentDimensions().parentHeight` doesn't
    // become a feedback loop (content height affecting itself).
    !useViewportHeight && "h-full min-h-0",
    props.className
  );

  const shouldUseSplit = Boolean(resizableSidebar && !isHidden && !hideSidebar);
  const collapsedSplitSizes = [96, 4];

  return (
    <>
      <div ref={parentRef} className={wrapperClass}>
        {shouldUseSplit ? (
          <Split
            // `h-full` allows it to fill its parent; `min-h-screen` ensures the
            // collapsed rail stays full height even when parent height is implicit.
            className={classNames(
              "split split-horizontal w-full min-w-0 h-full box-border",
              useViewportHeight ? "min-h-screen" : "min-h-0"
            )}
            direction="horizontal"
            sizes={open ? openSplitSizes : collapsedSplitSizes}
            minSize={[320, open ? openSidebarMinSize : collapsedSidebarMinSize]}
            gutterSize={open ? gutterSize : 0}
            snapOffset={0}
            onDragEnd={(sizes) => {
              // Dragging implies user intent to see the sidebar.
              if (!open) setOpen(true);
              setOpenSplitSizes(sizes);
            }}
          >
            <div className={contentClasses}>
              {!props.hideHeader && renderHeader(props)}

              <div ref={childRef} className={childWrapper}>
                {props.loading ? (
                  <div className="w-full flex justify-center items-center">
                    <Spinner />
                  </div>
                ) : (
                  children({
                    ...getContentDimensions(),
                    ...getContentStyles(),
                    headerRef,
                    headerContext,
                  })
                )}
              </div>
            </div>

            <aside
              className={asideClasses}
              onClick={() => !open && handleOpen()}
              ref={sidebarRef}
            >
              <div className="flex flex-col h-full">
                <div ref={headerRef} className={topClasses}>
                  <div className={sideTitleClass}>{props.sidebarTitle}</div>
                  <div className={iconWrapClasses} onClick={handleOpen}>
                    <Icon className="h-6 w-6" />
                  </div>
                </div>
                <div ref={childRef} className={childClasses}>
                  {typeof props.sidebarContent === "function"
                    ? props.sidebarContent({
                        ...getSidebarDimensions(),
                        ...getSidebarStyles(),
                        headerRef,
                        headerContext,
                      })
                    : props.sidebarContent}
                </div>
                {props.footer && (
                  <div
                    className={classNames(
                      footerClasses,
                      "flex-shrink-0" // Prevent footer from shrinking
                    )}
                    style={{ height: `${footerHeight}px` }} // Use fixed height instead of flex
                  >
                    {props.footer}
                  </div>
                )}
              </div>
            </aside>
          </Split>
        ) : (
          <>
            <div className={contentClasses}>
              {!props.hideHeader && renderHeader(props)}

              <div ref={childRef} className={childWrapper}>
                {props.loading ? (
                  <div className="w-full flex justify-center items-center">
                    <Spinner />
                  </div>
                ) : (
                  children({
                    ...getContentDimensions(),
                    ...getContentStyles(),
                    headerRef,
                    headerContext,
                  })
                )}
              </div>
            </div>

            {/* Sidebar */}
            {!hideSidebar && (
              <aside
                className={asideClasses}
                onClick={() => !open && handleOpen()}
                ref={sidebarRef}
              >
                <div className="flex flex-col h-full">
                  <div ref={headerRef} className={topClasses}>
                    <div className={sideTitleClass}>{props.sidebarTitle}</div>
                    <div className={iconWrapClasses} onClick={handleOpen}>
                      <Icon className="h-6 w-6" />
                    </div>
                  </div>
                  <div ref={childRef} className={childClasses}>
                    {typeof props.sidebarContent === "function"
                      ? props.sidebarContent({
                          ...getSidebarDimensions(),
                          ...getSidebarStyles(),
                          headerRef,
                          headerContext,
                        })
                      : props.sidebarContent}
                  </div>
                  {props.footer && (
                    <div
                      className={classNames(
                        footerClasses,
                        "flex-shrink-0" // Prevent footer from shrinking
                      )}
                      style={{ height: `${footerHeight}px` }} // Use fixed height instead of flex
                    >
                      {props.footer}
                    </div>
                  )}
                </div>
              </aside>
            )}
          </>
        )}
      </div>
      {isHidden && (
        <Drawer
          position="right"
          widthClasses="max-w-md"
          title={props.sidebarTitle}
          body={
            <div className="flex flex-col bg-gray-50 h-[calc(100vh-64px)]">
              <div className="flex-1 overflow-y-auto">
                {typeof props.sidebarContent === "function"
                  ? props.sidebarContent({
                      parentHeight: window.innerHeight,
                      parentWidth: window.innerWidth,
                      offsetTop: 0,
                      offsetHeight: 0,
                      wrapperStyle: {
                        height: "100%",
                      },
                      panelStyle: {
                        height: "100%",
                      },
                      headerRef,
                      headerContext,
                    })
                  : props.sidebarContent}
              </div>
              {props.footer && (
                <div
                  className="flex-shrink-0"
                  style={{ height: `${footerHeight}px` }}
                >
                  {props.footer}
                </div>
              )}
            </div>
          }
          visible={open}
          setVisible={setOpen}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};
